import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Spinner } from 'reactstrap';

import { Button } from '~components';
import { InputProps } from '~components/form-fields';
import * as FormInput from '~components/form-fields/inputs';
import Icon, { faPersonArrowDownToLine, faPersonArrowUpFromLine, IconProp } from '~components/icon';

import { selectErrorResponse, selectLocationDistance, selectLocationsLoading } from '~appointments/selectors/locations';

import { getDistance } from '../actions/locations';
import { clearDistance } from '../reducers/locations';

const FormInputLocations: React.FC<InputProps> = ({
  value,
  onChange,
  selectorOptions,
  selectorActions,
  selectorParams,
}) => {
  const dispatch = useAppDispatch();
  const distance = useAppSelector(selectLocationDistance);
  const locationsLoading = useAppSelector(selectLocationsLoading);
  const errorResponse = useAppSelector(selectErrorResponse);
  const [params, setParams] = useState<{ [key: string]: any }>({});

  const { client_id, transport_pickup_id = '', transport_dropoff_id = '', transport_pickup, transport_dropoff } = value;

  const initialiseTransportOption = (transport: any = {}) => {
    const { name: label, id: value } = transport ?? {};
    if (!label || !value) return;
    return { label, value };
  };

  useEffect(() => {
    setParams({ ...params, client_id });
  }, [client_id]);

  useEffect(() => {
    // The trip lives in the shared locations slice, so don't show one calculated for another appointment
    dispatch(clearDistance());
    setParams((prevParams) => ({ ...prevParams, client_id }));
    setChange('pickup_id', transport_pickup_id);
    setChange('dropoff_id', transport_dropoff_id);
  }, []);

  const clientHomeID = 100000;

  const setChange = (name: string, value: any) => {
    // Update calculate trip parameters based on user's current selection
    setParams((prevParams) => {
      const newParams = { ...prevParams };
      let homeParam;

      switch (name) {
        case 'pickup_id':
          homeParam = 'home_pickup';
          break;
        default:
        case 'dropoff_id':
          homeParam = 'home_dropoff';
          break;
      }

      if (value === clientHomeID) {
        newParams[homeParam] = true;
        delete newParams[name];
      } else {
        newParams[name] = value;
        delete newParams[homeParam];
      }

      return newParams;
    });

    onChange && onChange(`transport_${name}`, value);
  };

  const onCalculate = () => {
    dispatch(getDistance(params));
  };

  const buildSelect = (
    icon: IconProp,
    className: string,
    id: string,
    placeholder: string,
    value: any,
    initialValue: any,
  ) => {
    return (
      <>
        <Icon icon={icon} size="1x" className={className} />
        <FormInput.SelectLazy
          type="select"
          selectorActions={selectorActions}
          selectorParams={selectorParams}
          initialOption={{ id: clientHomeID, name: 'Client Home' }}
          selectorOptions={selectorOptions}
          initialValue={initialValue}
          dataCy={`${id}-select`}
          id={id}
          name={id}
          placeholder={placeholder}
          value={value}
          onChange={setChange}
        />
      </>
    );
  };

  const buildDistanceValue = () => {
    if (distance === undefined) return '';
    let message = `${Math.round(distance.distance)} billable KMs`;
    message += ` (Approx. ${Math.round(distance.duration)} mins)`;
    return message;
  };

  const isLoading = locationsLoading === 'pending';
  const showDistance = distance !== undefined;

  return (
    <>
      <div className="d-flex align-items-center ps-2 pe-2 pb-2" data-cy="trip-points">
        {buildSelect(
          faPersonArrowUpFromLine,
          'me-2',
          'pickup_id',
          'Pickup',
          transport_pickup_id,
          initialiseTransportOption(transport_pickup),
        )}
        {buildSelect(
          faPersonArrowDownToLine,
          'ms-2 me-2',
          'dropoff_id',
          'Drop Off',
          transport_dropoff_id,
          initialiseTransportOption(transport_dropoff),
        )}
      </div>
      {errorResponse && <div className="ps-2 pe-2 small text-danger">{errorResponse}</div>}
      <div className="d-flex align-items-center p-2">
        <Button
          type="button"
          size="sm"
          className="ms-4 me-4 ps-4 pe-4"
          color="success"
          onClick={onCalculate}
          disabled={isLoading}
          dataCy={'calculate_trip-button'}
        >
          {isLoading && <Spinner type="grow" size="sm" className="ms-4 me-4" />}
          {!isLoading && <span>Calculate Trip</span>}
        </Button>
        {showDistance && !isLoading && (
          <FormInput.ReadOnly type="readonly" name="km_estimate" id="km_estimate" value={buildDistanceValue()} />
        )}
      </div>
    </>
  );
};

export default FormInputLocations;
